import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toArray from "@babel/runtime/helpers/toArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import * as React from 'react'; // TODO: type definitions don't exist for this package (10/31/2020)
// @ts-ignore

import useResizeAware from 'react-resize-aware';
import { Box } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import Button from '../Button';
import { EditableInput } from '../Table/EditableInput';
import Spinner from '../Spinner';
import Typography from '../Typography';
import { designTokens } from '../Theme.design-tokens';
import { CenteredSpinner, DataSummaryActions, DataSummaryContainer, DataSummaryItem, DataSummaryItemInner, DataSummaryItemInnerLabel, DataSummaryItemInnerValue, DataSummaryOpacity, DataSummaryRoot, LabelInfoIcon, LabelInfoTypography, StyledErrorActionWrapper } from './styles';

/*
 * Why the trailing comma in the function declaration?
 * A: https://stackoverflow.com/questions/32308370/what-is-the-syntax-for-typescript-arrow-functions-with-generics/45576880#45576880
 */
var groupByThree = function groupByThree(_ref) {
  var _ref2 = _toArray(_ref),
      a = _ref2[0],
      b = _ref2[1],
      c = _ref2[2],
      rest = _ref2.slice(3);

  if (rest.length === 0) {
    return [[a, b, c].filter(function (x) {
      return x !== undefined;
    })];
  }

  return [[a, b, c]].concat(groupByThree(rest));
};

var prettyPrint = function prettyPrint(value) {
  if (value === null || value === undefined || value === '') {
    // Unicode hex value for "em dash": see https://unicodelookup.com/
    return "\u2014";
  }

  if (typeof value === 'boolean') {
    return String(value);
  }

  return value;
};

var isNotNull = function isNotNull(input) {
  return input != null;
};

var DataSummary = function DataSummary(_ref3) {
  var data = _ref3.data,
      dsOnEdit = _ref3.dsOnEdit,
      dsOnEditClickOverride = _ref3.dsOnEditClickOverride,
      dsOnSave = _ref3.dsOnSave,
      dsOnRefresh = _ref3.dsOnRefresh,
      e2e = _ref3.e2e,
      editMode = _ref3.editMode,
      error = _ref3.error,
      viewModeErrors = _ref3.viewModeErrors,
      fullWidth = _ref3.fullWidth,
      loading = _ref3.loading,
      summary = _ref3.summary,
      _ref3$maxColumns = _ref3.maxColumns,
      maxColumns = _ref3$maxColumns === void 0 ? 3 : _ref3$maxColumns;
  var theme = useTheme();
  var errorColor = theme.palette.error.main;

  var _React$useState = React.useState(_objectSpread({}, data)),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      clonedData = _React$useState2[0],
      setClonedData = _React$useState2[1];

  var _React$useState3 = React.useState(-1),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      labelTooltipIndex = _React$useState4[0],
      setLabelTooltipIndex = _React$useState4[1];

  React.useEffect(function () {
    setClonedData(_objectSpread({}, data));
  }, [data, editMode]);

  var _useResizeAware = useResizeAware(),
      _useResizeAware2 = _slicedToArray(_useResizeAware, 2),
      resizeListener = _useResizeAware2[0],
      sizes = _useResizeAware2[1];

  var labelRefs = React.useRef(summary.map(function () {
    return React.createRef();
  }));
  React.useEffect(function () {
    var labelElems = labelRefs.current.map(function (ref) {
      return ref.current;
    }).filter(isNotNull); // Apply effect on a per-group basis

    var groupedLabels = groupByThree(labelElems);
    groupedLabels.map(function (group) {
      /**
       * IE 11 requires assigning style properties to empty strings:
       * https://developer.mozilla.org/en-US/docs/Web/API/ElementCSSInlineStyle/style
       */
      group.forEach(function (label) {
        return label.style.height = '';
      }); // Only set height in 3-column mode

      if (window.innerWidth >= 600) {
        var maxLabelHeight = Math.max.apply(Math, _toConsumableArray(group.map(function (elem) {
          return elem.getBoundingClientRect().height;
        })));
        group.forEach(function (label) {
          return label.style.height = "".concat(maxLabelHeight, "px");
        });
      }
    });
  }, [sizes.width, sizes.height]);
  var conditional = {};
  var hasEditCapability = !!dsOnSave;
  var handleSave = React.useCallback(function () {
    Object.entries(clonedData).forEach(function (_ref4) {
      var _ref5 = _slicedToArray(_ref4, 2),
          key = _ref5[0],
          value = _ref5[1];

      var summaryItem = summary.find(function (_ref6) {
        var dataKey = _ref6.dataKey;
        return dataKey === key;
      });

      if ((summaryItem === null || summaryItem === void 0 ? void 0 : summaryItem.type) === 'integer' || (summaryItem === null || summaryItem === void 0 ? void 0 : summaryItem.type) === 'number') {
        clonedData[key] = value === '' ? undefined : Number(value);
      }
    });
    dsOnSave === null || dsOnSave === void 0 ? void 0 : dsOnSave(clonedData);
  }, [clonedData, dsOnSave, summary]);

  if (hasEditCapability) {
    conditional.editControls = /*#__PURE__*/React.createElement(DataSummaryActions, null, editMode && /*#__PURE__*/React.createElement(Button, _extends({
      a11yLabel: "Save"
    }, e2e && {
      e2e: "".concat(e2e, "-data-summary-save")
    }, {
      tooltip: "Save",
      icon: "save",
      dsOnClick: handleSave
    })), /*#__PURE__*/React.createElement(Button, _extends({
      a11yLabel: "Edit"
    }, e2e && {
      e2e: "".concat(e2e, "-data-summary-edit")
    }, {
      tooltip: "Edit",
      icon: "edit",
      variant: "outlined",
      dsOnClick: function dsOnClick() {
        if (dsOnEditClickOverride) {
          dsOnEditClickOverride();
        } else {
          dsOnEdit === null || dsOnEdit === void 0 ? void 0 : dsOnEdit(!editMode);
        }
      }
    })));
  }

  if (!!viewModeErrors && Object.keys(viewModeErrors).length) {
    conditional.editControls = /*#__PURE__*/React.createElement(StyledErrorActionWrapper, {
      errorColor: errorColor
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "error"
    }, "Data could not be loaded."), /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end"
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "overline2",
      color: "error"
    }, "RETRY"), /*#__PURE__*/React.createElement(Button, _extends({
      a11yLabel: "Refresh",
      tooltip: "Refresh"
    }, e2e && {
      e2e: "".concat(e2e, "-data-summary-refresh")
    }, {
      icon: "refresh",
      variant: "outlined",
      size: "small",
      dsOnClick: function dsOnClick() {
        // do the refresh
        dsOnRefresh === null || dsOnRefresh === void 0 ? void 0 : dsOnRefresh();
      }
    }))));
  }

  return /*#__PURE__*/React.createElement(DataSummaryRoot, _extends({
    fullWidth: fullWidth
  }, e2e && {
    'data-e2e': "".concat(e2e, "-data-summary")
  }), resizeListener, /*#__PURE__*/React.createElement(DataSummaryOpacity, {
    $loading: loading
  }, /*#__PURE__*/React.createElement(DataSummaryContainer, null, summary.map(function (_ref7, i) {
    var dataKey = _ref7.dataKey,
        label = _ref7.label,
        labelTooltip = _ref7.labelTooltip,
        RenderValue = _ref7.renderValue,
        type = _ref7.type,
        _ref7$labelProps = _ref7.labelProps,
        labelProps = _ref7$labelProps === void 0 ? {
      variant: 'overline'
    } : _ref7$labelProps,
        _ref7$inputProps = _ref7.inputProps,
        inputProps = _ref7$inputProps === void 0 ? {} : _ref7$inputProps,
        _ref7$columns = _ref7.columns,
        columns = _ref7$columns === void 0 ? 1 : _ref7$columns;
    var _ref8 = inputProps,
        inputError = _ref8.error,
        helperText = _ref8.helperText,
        disabled = _ref8.disabled,
        _onChange = _ref8.onChange,
        onBlur = _ref8.onBlur,
        onFocus = _ref8.onFocus,
        dateFormat = _ref8.dateFormat,
        minDate = _ref8.minDate,
        maxDate = _ref8.maxDate,
        picklistOptions = _ref8.picklistOptions,
        picklistEmptyOption = _ref8.picklistEmptyOption,
        typeaheadOptions = _ref8.typeaheadOptions,
        typeaheadLoading = _ref8.typeaheadLoading,
        typeaheadGetOptionLabel = _ref8.typeaheadGetOptionLabel,
        typeaheadRenderOption = _ref8.typeaheadRenderOption,
        multiline = _ref8.multiline,
        rows = _ref8.rows,
        maxlength = _ref8.maxlength,
        required = _ref8.required;
    var hasFetchError = viewModeErrors === null || viewModeErrors === void 0 ? void 0 : viewModeErrors[dataKey];
    return /*#__PURE__*/React.createElement(DataSummaryItem, _extends({
      key: dataKey,
      $editMode: editMode,
      $columns: columns / maxColumns * 100
    }, e2e && {
      'data-e2e': "".concat(e2e, "-data-summary-item-").concat(i)
    }), /*#__PURE__*/React.createElement(DataSummaryItemInner, e2e && {
      'data-e2e': "".concat(e2e, "-data-summary-item-inner-").concat(i)
    }, /*#__PURE__*/React.createElement(DataSummaryItemInnerLabel, {
      ref: labelRefs.current[i],
      textColor: hasFetchError ? errorColor : null,
      onMouseEnter: function onMouseEnter() {
        return setLabelTooltipIndex(i);
      },
      onMouseLeave: function onMouseLeave() {
        return setLabelTooltipIndex(-1);
      }
    }, /*#__PURE__*/React.createElement(Typography, _extends({
      color: labelProps.color || 'textSecondary',
      variant: labelProps.variant
    }, e2e && {
      e2e: "".concat(e2e, "-data-summary-").concat(dataKey, "-label")
    }), label, ' ', required && /*#__PURE__*/React.createElement(Box, {
      display: "inline-block",
      height: 8,
      width: 8,
      borderRadius: "50%",
      bgcolor: designTokens.colors.teal600
    })), labelTooltip && /*#__PURE__*/React.createElement(LabelInfoTypography, {
      body: /*#__PURE__*/React.createElement(LabelInfoIcon, {
        body: "help_outline"
      }),
      component: "span",
      tooltip: labelTooltip,
      tooltipOpen: labelTooltipIndex === i
    })), editMode && type ? /*#__PURE__*/React.createElement(EditableInput, _extends({}, e2e && {
      e2e: "".concat(e2e, "-data-summary-").concat(dataKey)
    }, {
      disabled: disabled || loading,
      error: Boolean(error === null || error === void 0 ? void 0 : error[dataKey]) || Boolean(inputError),
      helperText: helperText,
      value: clonedData[dataKey],
      onChange: function onChange(value) {
        _onChange === null || _onChange === void 0 ? void 0 : _onChange(value);
        setClonedData(_objectSpread(_objectSpread({}, clonedData), {}, _defineProperty({}, dataKey, value)));
      },
      onBlur: onBlur,
      onFocus: onFocus,
      type: type
    }, {
      dateFormat: dateFormat,
      minDate: minDate,
      maxDate: maxDate,
      picklistOptions: picklistOptions,
      picklistEmptyOption: picklistEmptyOption,
      picklistUniqueKey: dataKey,
      typeaheadOptions: typeaheadOptions,
      typeaheadLoading: typeaheadLoading,
      typeaheadGetOptionLabel: typeaheadGetOptionLabel,
      typeaheadRenderOption: typeaheadRenderOption,
      multiline: multiline,
      rows: rows,
      maxlength: maxlength
    })) : /*#__PURE__*/React.createElement(DataSummaryItemInnerValue, _extends({}, e2e && {
      'data-e2e': "".concat(e2e, "-data-summary-").concat(dataKey, "-value")
    }, {
      color: hasFetchError && errorColor
    }), RenderValue ? /*#__PURE__*/React.createElement(RenderValue, null) : prettyPrint(data[dataKey]))));
  })), conditional.editControls), loading ? /*#__PURE__*/React.createElement(CenteredSpinner, null, /*#__PURE__*/React.createElement(Spinner, null)) : null);
};

export default DataSummary;

try {
  DataSummary.__docgenInfo = {
    description: '',
    displayName: 'DataSummary',
    methods: [],
    props: {
      data: {
        defaultValue: null,
        description: 'Object containing values.\n' + 'Only flat objects are supported.\n' + 'No nested objects.',
        name: 'data',
        required: true,
        type: {
          name: '{ [key: string]: any; }'
        }
      },
      e2e: {
        defaultValue: null,
        description: '',
        name: 'e2e',
        required: false,
        type: {
          name: 'string'
        }
      },
      error: {
        defaultValue: null,
        description: "Error object.\nObject property names must line up with 'data' object.",
        name: 'error',
        required: false,
        type: {
          name: '{ [key: string]: string; }'
        }
      },
      viewModeErrors: {
        defaultValue: null,
        description: 'An error object to convey field level data fetching failure',
        name: 'viewModeErrors',
        required: false,
        type: {
          name: '{ [key: string]: any; }'
        }
      },
      editMode: {
        defaultValue: null,
        description: "Set to 'true' to enable edit mode.",
        name: 'editMode',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: 'Assume parent container width and omit inner margin.',
        name: 'fullWidth',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      loading: {
        defaultValue: null,
        description: "Set to 'true' for a loading visual treatment. Use when\n" + 'performing a network request to mutate data in data summary.',
        name: 'loading',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      dsOnEdit: {
        defaultValue: null,
        description: "Called when 'edit' button is clicked.",
        name: 'dsOnEdit',
        required: false,
        type: {
          name: '(editMode: boolean) => void'
        }
      },
      dsOnEditClickOverride: {
        defaultValue: null,
        description: 'Overrides "dsOnEdit". This will allow the button to be clicked without\n' + 'toggling the view state into edit mode.',
        name: 'dsOnEditClickOverride',
        required: false,
        type: {
          name: '(_?: any) => void'
        }
      },
      dsOnSave: {
        defaultValue: null,
        description: 'Pass this function to enable edit capabilities.',
        name: 'dsOnSave',
        required: false,
        type: {
          name: '(data: { [key: string]: any; }) => void'
        }
      },
      dsOnRefresh: {
        defaultValue: null,
        description: 'A function invoked to re-request the DataSummary data',
        name: 'dsOnRefresh',
        required: false,
        type: {
          name: '() => void'
        }
      },
      summary: {
        defaultValue: null,
        description: '',
        name: 'summary',
        required: true,
        type: {
          name: 'DataSummaryPropsSummary[]'
        }
      },
      maxColumns: {
        defaultValue: {
          value: 3
        },
        description: 'The total number of columns a DataSummary should have',
        name: 'maxColumns',
        required: false,
        type: {
          name: 'number'
        }
      }
    }
  };
} catch (e) {}
//# sourceMappingURL=index.js.map